import React, { FC } from 'react';
import styled from 'styled-components';
import { Collector } from '../../models/Collector';
import { COLORS } from '../../utils/Colors';

const P = styled.p`
    padding: 12px;
    background-color: ${COLORS.WHITE};
    border: 3px solid ${COLORS.BLACK};
    white-space: pre;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.2;
    
    @media screen and (max-width: 768px) {
        padding: 8px;
        font-size: 12px;
        border: 2px solid ${COLORS.BLACK};
        overflow-x: auto;
    }
    
    @media screen and (max-width: 480px) {
        padding: 6px;
        font-size: 10px;
        border: 1px solid ${COLORS.BLACK};
    }
`;

interface Props {
  collector: Collector;
}

interface PinData {
  name: string;
  value: string;
}

interface HardwareStatus {
  ready: boolean;
  problems: Array<{ name: string; value: string }>;
}

const parseDiagnostics = (diagnostics: string): string => {
  try {
    let output = '';
    
    // Extract everything before the Input PIN section (status changes, timestamps, etc.)
    const beforePinsMatch = diagnostics.match(/^([\s\S]*?)(?=Input PIN)/);
    if (beforePinsMatch) {
      output += beforePinsMatch[1].trim() + '\n\n';
    }

    // Extract the Input PIN section with its original header and formatting
    const pinSectionMatch = diagnostics.match(/Input PIN.*?Value\s*\n([\s\S]*?)(?=\n\n|\nSystem status)/);
    if (pinSectionMatch) {
      // Extract the header line
      const headerMatch = diagnostics.match(/(Input PIN.*?Value\s*\n)/);
      if (headerMatch) {
        output += headerMatch[1];
      }
      
      // Extract all pin lines
      const pinLines = pinSectionMatch[1].split('\n').filter(line => line.trim());
      pinLines.forEach(line => {
        if (line.trim()) {
          output += line + '\n';
        }
      });
      output += '\n';
    }

    // Extract the System status section with its original header and formatting
    const systemStatusMatch = diagnostics.match(/(System status.*?Value\s*\n)([-\s]+)\n([\s\S]*?)(?=\n\n|\nhardware_status|$)/);
    if (systemStatusMatch) {
      output += systemStatusMatch[1]; // Header
      output += systemStatusMatch[2] + '\n'; // Separator
      
      // Extract system status lines
      const statusLines = systemStatusMatch[3].split('\n').filter(line => line.trim());
      statusLines.forEach(line => {
        if (line.trim()) {
          output += line + '\n';
        }
      });
      output += '\n';
    }

    // Extract hardware_status section
    const hardwareStatusMatch = diagnostics.match(/hardware_status:\s*({[\s\S]*?})\s*\n/);
    if (hardwareStatusMatch) {
      output += 'hardware_status:\n';
      output += hardwareStatusMatch[1] + '\n';
    }

    // Extract in_operating_time
    const operatingTimeMatch = diagnostics.match(/in_operating_time:\s*(\w+)/);
    if (operatingTimeMatch) {
      output += `in_operating_time: ${operatingTimeMatch[1]}`;
    }
    
    return output.trim();
  } catch (error) {
    console.error('Error parsing diagnostics:', error);
    return diagnostics; // Fallback to original text
  }
};

export const CollectorDiagnosticWidget: FC<Props> = ({ collector }) => (
  <P>
    {parseDiagnostics(collector.diagnostics || '')}
  </P>
);
